import { isArrayNullOrEmpty } from '@farmshare/utils';
import { concat, join, map } from 'lodash';
import { useCallback } from 'react';
import { useFormikContext } from 'formik';

import { InputPhone, InputSelect, InputText } from '../inputs';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { GoogleAddressSearch } from './google-address-search';

export interface AddressFormForm extends Record<string, string | undefined> {
  type: 'residential' | 'commercial' | 'unknown';
  firstName?: string;
  lastName?: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  email?: string;
  phone?: string;
  /** GPS latitude, populated automatically when using Google address search */
  latitude?: string;
  /** GPS longitude, populated automatically when using Google address search */
  longitude?: string;
}

export interface AddressFormProps {
  disabled?: boolean;
  floatingLabel?: boolean;
  prefixes?: string[];
  readOnly?: boolean;
  states?: {
    name: string;
    code: string;
  }[];
  /** When supplied, shows a Google Maps address search field that auto-fills the form fields */
  googleMapsApiKey?: string;
}

export function AddressForm({
  disabled = false,
  floatingLabel = false,
  prefixes = undefined,
  readOnly = false,
  states = undefined,
  googleMapsApiKey = undefined,
}: AddressFormProps) {
  const { setFieldValue } = useFormikContext<AddressFormForm>();

  const nameOverride = useCallback(
    (name: string) => {
      if (!isArrayNullOrEmpty(prefixes)) {
        return join(concat(prefixes, name), '.');
      }

      return name;
    },
    [prefixes],
  );

  const handleGoogleSelect = useCallback(
    (parsed: {
      address1: string;
      city: string;
      state: string;
      postcode: string;
      latitude: number | null;
      longitude: number | null;
    }) => {
      setFieldValue(nameOverride('address1'), parsed.address1);
      setFieldValue(nameOverride('city'), parsed.city);
      setFieldValue(nameOverride('state'), parsed.state);
      setFieldValue(nameOverride('postcode'), parsed.postcode);
      // Store coordinates as strings to stay consistent with the form interface
      if (parsed.latitude !== null) {
        setFieldValue(nameOverride('latitude'), String(parsed.latitude));
      }
      if (parsed.longitude !== null) {
        setFieldValue(nameOverride('longitude'), String(parsed.longitude));
      }
    },
    [nameOverride, setFieldValue],
  );

  return (
    <Stack spacing={1}>
      {/* Google Maps address search — shown only when an API key is provided and form is editable */}
      {googleMapsApiKey && !readOnly && !disabled && (
        <>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={600}
          >
            Search address
          </Typography>
          <GoogleAddressSearch
            apiKey={googleMapsApiKey}
            onSelect={handleGoogleSelect}
            disabled={disabled}
          />
          <Divider sx={{ display: 'block', my: 1 }} />
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={600}
          >
            Address details
          </Typography>
        </>
      )}
      <Stack direction="row" spacing={1}>
        <InputText
          label="First Name"
          nameOverride={nameOverride('firstName')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
        <InputText
          label="Last Name"
          nameOverride={nameOverride('lastName')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
      </Stack>
      <InputText
        label="Company"
        nameOverride={nameOverride('company')}
        type="text"
        floatingLabel={floatingLabel}
        readOnly={readOnly}
        disabled={disabled}
      />
      <Stack direction="row" spacing={1}>
        <InputText
          label="Email"
          nameOverride={nameOverride('email')}
          type="email"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
        <InputPhone
          label="Phone"
          nameOverride={nameOverride('phone')}
          floatingLabel={floatingLabel}
          required
          readOnly={readOnly}
          disabled={disabled}
        />
      </Stack>
      <Divider
        sx={{
          display: 'block',
          my: (theme) => `${theme.spacing(2)} !important`,
        }}
      />
      <InputText
        label="Address 1"
        nameOverride={nameOverride('address1')}
        type="text"
        floatingLabel={floatingLabel}
        readOnly={readOnly}
        disabled={disabled}
        required
      />
      <Stack direction="row" spacing={1}>
        <InputText
          label="Address 2"
          nameOverride={nameOverride('address2')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
        />
        <InputSelect
          label="Address Type"
          options={[
            {
              value: 'residential',
              label: 'Residential',
            },
            {
              value: 'commercial',
              label: 'Commercial',
            },
            {
              value: 'unknown',
              label: 'Unknown',
            },
          ]}
          nameOverride={nameOverride('type')}
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
          sx={{ flexBasis: 0.3, minWidth: 160 }}
        />
      </Stack>
      <Stack direction="row" spacing={1}>
        <InputText
          label="City"
          nameOverride={nameOverride('city')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
        <InputSelect
          label="State"
          options={map(states, (s) => ({
            value: s.code,
            label: s.name,
          }))}
          nameOverride={nameOverride('state')}
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
        <InputText
          label="Zip"
          nameOverride={nameOverride('postcode')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
      </Stack>
      {/* Hidden GPS coordinate fields — populated automatically by Google
          address search. `type="hidden"` only hides the native input;
          wrapping in a `display: none` box also collapses MUI's outer
          FormControl so the form has no blank rows. */}
      <Box sx={{ display: 'none' }}>
        <InputText
          nameOverride={nameOverride('latitude')}
          label="Latitude"
          type="hidden"
          hidden
        />
        <InputText
          nameOverride={nameOverride('longitude')}
          label="Longitude"
          type="hidden"
          hidden
        />
      </Box>
    </Stack>
  );
}
